/**
 * The discard field: four pools of tiles lying face up inside the wall.
 *
 * Discards are public - every player at a real table can see and count them -
 * so this is the one place opponent tile kinds legitimately appear. Each pool
 * is laid out the way a player lays their own discards down, six to a row, and
 * the whole pool is turned to face its owner, so the field reads as four people
 * around one table rather than four lists.
 *
 * The last tile played is marked, because "what was just discarded" is the
 * single question a beginner asks most often. Nothing else about a pool is
 * highlighted: which tiles are claimable is a decision the claim bar states in
 * words, not something the felt hints at.
 */

import type { PlayerId } from "@kikik/mahjong-core";
import { useI18n } from "../i18n";
import type { TableModel } from "../game/tableModel";
import { seatSide } from "../game/tableModel";
import { MahjongTile } from "./tiles";

export interface DiscardFieldProps {
  model: TableModel;
  nameFor: (playerId: PlayerId) => string;
}

export function DiscardField({ model, nameFor }: DiscardFieldProps) {
  const { t } = useI18n();
  const latestId = model.lastDiscard?.tile.id ?? null;

  return (
    <div className="discards" data-testid="discard-field">
      {model.seats.map((seat) => {
        const side = seatSide(seat.id, model.you);
        const name = nameFor(seat.id);
        return (
          <div
            key={seat.id}
            className={`discards__pool discards__pool--${side}`}
            data-side={side}
            data-seat={seat.id}
            data-count={seat.discards.length}
          >
            <div
              className="seat__discards"
              {...(seat.isYou ? { "data-testid": "your-discards" } : {})}
              aria-label={
                seat.isYou
                  ? t("table.yourDiscards")
                  : t("seat.discardsOf", { player: name })
              }
            >
              {seat.discards.map((tile, index) => (
                <MahjongTile
                  key={tile.id}
                  kind={tile.kind}
                  size="xs"
                  laid
                  latest={latestId === tile.id}
                  index={index}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
